import {
  BookOpenIcon,
  ClipboardCheckIcon,
  Code2Icon,
  FlaskConicalIcon,
  type LucideIcon,
  NetworkIcon,
  PlayIcon,
  VideoIcon,
} from 'lucide-react'
import type { TaskType } from './types'

export interface TaskTypeConfig {
  label: string
  icon: LucideIcon
  /** text color class for the icon */
  text: string
  /** soft background chip class */
  chip: string
  /** solid dot class for calendar */
  dot: string
  /** css variable used by charts */
  colorVar: string
}

export const TASK_TYPE_CONFIG: Record<TaskType, TaskTypeConfig> = {
  leetcode: {
    label: 'LeetCode',
    icon: Code2Icon,
    text: 'text-task-leetcode',
    chip: 'bg-task-leetcode/12 text-task-leetcode',
    dot: 'bg-task-leetcode',
    colorVar: 'var(--task-leetcode)',
  },
  'system-design': {
    label: 'System Design',
    icon: NetworkIcon,
    text: 'text-task-design',
    chip: 'bg-task-design/12 text-task-design',
    dot: 'bg-task-design',
    colorVar: 'var(--task-design)',
  },
  video: {
    label: 'Video',
    icon: PlayIcon,
    text: 'text-task-video',
    chip: 'bg-task-video/12 text-task-video',
    dot: 'bg-task-video',
    colorVar: 'var(--task-video)',
  },
  article: {
    label: 'Article',
    icon: BookOpenIcon,
    text: 'text-task-article',
    chip: 'bg-task-article/12 text-task-article',
    dot: 'bg-task-article',
    colorVar: 'var(--task-article)',
  },
  exercise: {
    label: 'Coding Exercise',
    icon: FlaskConicalIcon,
    text: 'text-task-exercise',
    chip: 'bg-task-exercise/12 text-task-exercise',
    dot: 'bg-task-exercise',
    colorVar: 'var(--task-exercise)',
  },
  meeting: {
    label: 'Meeting',
    icon: VideoIcon,
    text: 'text-task-meeting',
    chip: 'bg-task-meeting/12 text-task-meeting',
    dot: 'bg-task-meeting',
    colorVar: 'var(--task-meeting)',
  },
  task: {
    label: 'Task',
    icon: ClipboardCheckIcon,
    text: 'text-task-generic',
    chip: 'bg-task-generic/12 text-task-generic',
    dot: 'bg-task-generic',
    colorVar: 'var(--task-generic)',
  },
}

export const TASK_TYPES: TaskType[] = [
  'leetcode',
  'system-design',
  'video',
  'article',
  'exercise',
  'meeting',
  'task',
]

// ---------------------------------------------------------------------------
// Track identity — shared accent colors for the three interview pillars,
// used by the mastery section and extra credit.
// ---------------------------------------------------------------------------

export type Track = 'coding' | 'system-design' | 'behavioral'

export interface TrackIdentity {
  label: string
  icon: LucideIcon
  /** soft chip background + text */
  chip: string
  /** solid bar fill */
  bar: string
  /** text color */
  text: string
  /** soft border accent */
  border: string
  /** active-tab tint (applied on data-[state=active]) */
  activeTab: string
  href: string
}

export const TRACK_IDENTITY: Record<Track, TrackIdentity> = {
  coding: {
    label: 'Coding',
    icon: Code2Icon,
    chip: 'bg-task-leetcode/15 text-task-leetcode',
    bar: 'bg-task-leetcode',
    text: 'text-task-leetcode',
    border: 'border-task-leetcode/40',
    activeTab:
      'data-[state=active]:border-task-leetcode/50 data-[state=active]:bg-task-leetcode/10 data-[state=active]:text-task-leetcode data-[state=active]:shadow-none',
    href: '/problems',
  },
  'system-design': {
    label: 'System design',
    icon: NetworkIcon,
    chip: 'bg-task-design/15 text-task-design',
    bar: 'bg-task-design',
    text: 'text-task-design',
    border: 'border-task-design/40',
    activeTab:
      'data-[state=active]:border-task-design/50 data-[state=active]:bg-task-design/10 data-[state=active]:text-task-design data-[state=active]:shadow-none',
    href: '/problems',
  },
  behavioral: {
    label: 'Behavioral',
    icon: BookOpenIcon,
    chip: 'bg-task-article/15 text-task-article',
    bar: 'bg-task-article',
    text: 'text-task-article',
    border: 'border-task-article/40',
    activeTab:
      'data-[state=active]:border-task-article/50 data-[state=active]:bg-task-article/10 data-[state=active]:text-task-article data-[state=active]:shadow-none',
    href: '/problems',
  },
}
